import { useLang } from "../../context/LanguageContext";
import { useContent, photoUrl } from "../../context/ContentContext";
import { HERO_IMAGE } from "../../data/content";
import { Star, MapPin, Car } from "lucide-react";
import { motion } from "framer-motion";

export const Hero = () => {
  const { t, lang } = useLang();
  const { content } = useContent();
  const specs = t("hero.specs");
  const heroImg = content.hero_photo_id ? photoUrl(content.hero_photo_id) : HERO_IMAGE;
  const title = content.hero?.title?.[lang] || t("hero.title");
  const subtitle = content.hero?.subtitle?.[lang] || t("hero.subtitle");

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden">
      <img src={heroImg} alt="The Cozy Nest" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/40" />

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-4 sm:px-8 pb-16 pt-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <div className="flex flex-wrap items-center gap-3 text-white/90">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] backdrop-blur-sm">
              <MapPin className="h-3.5 w-3.5" /> {t("hero.eyebrow")}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C85A32] px-3 py-1 text-xs font-semibold text-white" data-testid="hero-rating">
              <Star className="h-3.5 w-3.5 fill-white" /> {t("hero.rating")}
            </span>
          </div>

          <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight text-white">
            {title}
          </h1>
          <p className="mt-5 max-w-xl text-lg sm:text-xl leading-relaxed text-white/90">
            {subtitle}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              data-testid="hero-booking-cta"
              onClick={() => scrollTo("booking")}
              className="rounded-full bg-[#C85A32] px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-[#B04B26]"
            >
              {t("hero.cta")}
            </button>
            <button
              onClick={() => scrollTo("gallery")}
              className="rounded-full border border-white/40 bg-white/10 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              {t("nav.gallery")}
            </button>
            <button
              data-testid="hero-car-cta"
              onClick={() => scrollTo("carrental")}
              className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              <Car className="h-4 w-4" />
              {t("hero.carCta")}
            </button>
          </div>

          <div className="mt-9 flex flex-wrap gap-2.5">
            {Array.isArray(specs) &&
              specs.map((s, i) => (
                <span
                  key={i}
                  className="rounded-full border border-white/25 bg-black/20 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm"
                >
                  {s}
                </span>
              ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
